"use client";

import React, { useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

interface Props {
    children: React.ReactNode;
}

const OfferSlider = ({ children }: Props) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);

    const slideCount = React.Children.count(children);

    // Desktop layout
    const desktopPerView =
        slideCount === 1
            ? 1
            : slideCount === 2
                ? 2
                : slideCount === 3
                    ? 3
                    : 4.25;

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        initial: 0,

        // Don't loop when there aren't enough cards
        loop: slideCount > 4,

        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },

        created() {
            setLoaded(true);
        },

        mode: "snap",

        slides: {
            perView: desktopPerView,
            spacing: 10,
        },

        breakpoints: {
            "(max-width: 1200px)": {
                slides: {
                    perView:
                        slideCount === 1
                            ? 1
                            : slideCount === 2
                                ? 2
                                : slideCount === 3
                                    ? 3
                                    : 4.25,
                    spacing: 10,
                },
            },

            "(max-width: 992px)": {
                slides: {
                    perView:
                        slideCount === 1
                            ? 1
                            : slideCount === 2
                                ? 2
                                : 2.25,
                    spacing: 10,
                },
            },

            "(max-width: 576px)": {
                slides: {
                    perView: slideCount === 1 ? 1 : 1.2,
                    spacing: 10,
                    origin: slideCount === 1 ? "auto" : "center",
                },
            },
        },
    });

    return (
        <div className="slider-wrapper">
            {/* When 4 or fewer items — centered flex row, no slider needed */}
            {slideCount <= 4 ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "10px",
                        flexWrap: "wrap",
                    }}
                >
                    {React.Children.map(children, (child) =>
                        React.isValidElement(child) ? (
                            <div style={{ flex: "0 0 calc(25% - 8px)", minWidth: "200px", maxWidth: "280px" }}>
                                {child}
                            </div>
                        ) : child
                    )}
                </div>
            ) : (
                <>
                    <div ref={sliderRef} className="keen-slider">
                        {React.Children.map(children, (child) => {
                            if (React.isValidElement(child)) {
                                return (
                                    <div className="keen-slider__slide">
                                        {child}
                                    </div>
                                );
                            }
                            return child;
                        })}
                    </div>

                    {/* Arrows */}
                    {loaded && instanceRef.current && (
                        <>
                            <Arrow
                                left
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    instanceRef.current?.prev();
                                }}
                            />
                            <Arrow
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    instanceRef.current?.next();
                                }}
                            />
                        </>
                    )}

                    {/* Dots */}
                    {loaded && instanceRef.current && (
                        <div className="dots">
                            {[
                                ...Array(
                                    instanceRef.current.track.details.slides.length
                                ).keys(),
                            ].map((idx) => (
                                <button
                                    key={idx}
                                    onClick={() => instanceRef.current?.moveToIdx(idx)}
                                    className={"dot" + (currentSlide === idx ? " active" : "")}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

function Arrow(props: {
    disabled?: boolean;
    left?: boolean;
    onClick: (e: any) => void;
}) {
    const disabledClass = props.disabled
        ? " arrow--disabled"
        : "";

    return (
        <div
            onClick={props.onClick}
            className={`custom-keen-arrow ${props.left ? "arrow--left" : "arrow--right"
                } ${disabledClass}`}
        >
            <FontAwesomeIcon
                icon={
                    props.left
                        ? faChevronLeft
                        : faChevronRight
                }
            />
        </div>
    );
}

export default OfferSlider;